import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  TablePagination,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import DirectionsBusIcon from '@material-ui/icons/DirectionsBus';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import StatusChip from '../../components/ui/StatusChip';
import SearchBar from '../../components/ui/SearchBar';
import { exportToCsv } from '../../utils/exportCsv';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  search: { maxWidth: 300, flexGrow: 1 },
  count: { color: theme.palette.text.secondary, fontSize: '0.85rem' },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  vehicleCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  vehicleAvatar: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color: '#fff',
  },
  vehicleName: { fontWeight: 600 },
  sub: { fontSize: '0.75rem', color: theme.palette.text.secondary },
}));

const STATUS_NORM = {
  Active: 'active',
  active: 'active',
  Available: 'active',
  available: 'active',
  Inactive: 'inactive',
  inactive: 'inactive',
  Maintenance: 'pending',
  maintenance: 'pending',
};

const STATUS_LABEL = {
  active: 'Disponible',
  inactive: 'Inactif',
  pending: 'En maintenance',
};

const VehiclesListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(firestore, 'vehicles'));
      setVehicles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la récupération des véhicules', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const handleDeleteConfirm = async () => {
    if (!vehicleToDelete) return;
    try {
      await deleteDoc(doc(firestore, 'vehicles', vehicleToDelete.id));
      setVehicles((prev) => prev.filter((v) => v.id !== vehicleToDelete.id));
      setSnackbar({ open: true, message: 'Véhicule supprimé', severity: 'success' });
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setVehicleToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return vehicles.filter((v) => {
      const matchSearch =
        (v.name || '').toLowerCase().includes(q) ||
        (v.registrationNumber || v.licensePlate || '').toLowerCase().includes(q) ||
        (v.type || '').toLowerCase().includes(q) ||
        (v.companyName || '').toLowerCase().includes(q) ||
        (v.model || '').toLowerCase().includes(q);
      const norm = STATUS_NORM[v.status] || 'inactive';
      const matchStatus = statusFilter === 'all' || norm === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [vehicles, searchTerm, statusFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExport = () => {
    exportToCsv(
      filtered,
      ['name', 'registrationNumber', 'type', 'capacity', 'companyName', 'status'],
      {
        name: 'Nom',
        registrationNumber: 'Immatriculation',
        type: 'Type',
        capacity: 'Capacité',
        companyName: 'Entreprise',
        status: 'Statut',
      },
      'vehicules'
    );
  };

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Véhicules' }]}
        title="Véhicules"
        subtitle={`${filtered.length} véhicule${filtered.length > 1 ? 's' : ''}`}
        actions={
          <>
            <Tooltip title="Exporter CSV">
              <IconButton onClick={handleExport} style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rafraîchir">
              <IconButton onClick={fetchVehicles} style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/vehicles/create')}
            >
              Nouveau véhicule
            </Button>
          </>
        }
      />

      <Paper>
        <Box className={classes.toolbar}>
          <Box className={classes.search}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0); }}
              placeholder="Rechercher par nom, immatriculation, modèle…"
            />
          </Box>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="active">Disponible</MenuItem>
              <MenuItem value="pending">En maintenance</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
            </Select>
          </FormControl>
          <Typography className={classes.count}>
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </Typography>
        </Box>

        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<DirectionsBusIcon style={{ fontSize: 36 }} />}
            title="Aucun véhicule"
            description={
              searchTerm || statusFilter !== 'all'
                ? 'Aucun véhicule ne correspond à vos filtres.'
                : 'Commencez par ajouter un premier véhicule.'
            }
            actionLabel={!searchTerm && statusFilter === 'all' ? 'Ajouter un véhicule' : undefined}
            onAction={() => navigate('/admin/vehicles/create')}
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Véhicule</TableCell>
                    <TableCell>Immatriculation</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Capacité</TableCell>
                    <TableCell>Entreprise</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((v) => {
                    const norm = STATUS_NORM[v.status] || 'inactive';
                    return (
                      <TableRow key={v.id} hover>
                        <TableCell>
                          <Box className={classes.vehicleCell}>
                            <Avatar className={classes.vehicleAvatar}>
                              <DirectionsBusIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Box className={classes.vehicleName}>{v.name || '—'}</Box>
                              <Box className={classes.sub}>{v.model || ''}</Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{v.registrationNumber || v.licensePlate || '—'}</TableCell>
                        <TableCell>{v.type || '—'}</TableCell>
                        <TableCell>{v.capacity ? `${v.capacity} places` : '—'}</TableCell>
                        <TableCell>{v.companyName || '—'}</TableCell>
                        <TableCell>
                          <StatusChip status={norm} label={STATUS_LABEL[norm] || v.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Voir">
                            <IconButton size="small" onClick={() => navigate(`/admin/vehicles/${v.id}`)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifier">
                            <IconButton size="small" onClick={() => navigate(`/admin/vehicles/${v.id}/edit`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              style={{ color: '#ef4444' }}
                              onClick={() => { setVehicleToDelete(v); setOpenDeleteDialog(true); }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Par page"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            />
          </>
        )}
      </Paper>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer le véhicule <strong>{vehicleToDelete?.name}</strong>{' '}
            ({vehicleToDelete?.registrationNumber || vehicleToDelete?.licensePlate}) ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            style={{ background: '#ef4444', color: '#fff' }}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehiclesListPage;
